import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AppModal from '../../../components/AppModal';
import StudentForm from '../../../components/StudentForm';
import { ApiError, StudentFormData, UserData } from '../../../services/ApiService.types';
import { success, error } from '../../../components/Message';
import { editStudent, refreshStudentsPage, setEditingStudentError } from '../slice/actions';
import useActionStatus from '../../../hooks/useActionStatus';
import { getActivationLinkSentTo, getIsEditingError, getIsEditing } from '../slice/selectors';
import ErrorMessage from '../../../components/ErrorMessage';
import { polishServerText } from '../../../utils/apiErrors';

type EditStudentModalProps = {
  isVisible: boolean;
  hideModal: () => void;
  student: UserData;
};

const EditStudentModal: FC<EditStudentModalProps> = ({ student, isVisible, hideModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector(getIsEditing);
  const apiError = useSelector(getIsEditingError);
  const activationLinkSentTo = useSelector(getActivationLinkSentTo);

  const onSubmit = useCallback((values: StudentFormData) => {
    if (student.id) {
      dispatch(editStudent(student.id, values));
    }
  }, [dispatch, student.id]);

  const cleanupError = useCallback(() => {
    dispatch(setEditingStudentError(null));
  }, [dispatch]);

  const onCancel = useCallback(() => {
    hideModal();
    cleanupError();
  }, [hideModal, cleanupError]);

  const onSuccess = useCallback(() => {
    success(activationLinkSentTo
      ? t('student:edit.messages.linkSent', { email: activationLinkSentTo })
      : t('student:edit.messages.success'));
    hideModal();
  }, [activationLinkSentTo, hideModal, t]);

  const onError = useCallback((editError: ApiError) => {
    // Ucznia już nie ma, np. usunięty w innej karcie — okno nie ma czego edytować
    if (editError.code === 'STUDENT_NOT_FOUND') {
      error(polishServerText(editError.message) || t('student:gone'));
      dispatch(refreshStudentsPage());
      onCancel();

      return;
    }

    error(t('student:edit.messages.error'));
  }, [dispatch, onCancel, t]);

  useActionStatus(onSuccess, onError, {
    isLoadingSelector: getIsEditing,
    errorSelector: getIsEditingError,
  });

  return (
    <AppModal title={t('student:edit.title')} visible={isVisible} onCancel={onCancel}>
      {/* Tekst backendu tylko przy odpowiedzi z kodem — inne błędy zgłasza samo powiadomienie */}
      {apiError?.code && polishServerText(apiError.message) && <ErrorMessage>{apiError.message}</ErrorMessage>}
      <StudentForm student={student} isLoading={isLoading} onSubmit={onSubmit} />
    </AppModal>
  );
};

export default EditStudentModal;
